import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import {
  CreateProductPriceRequest,
  EditProductPriceRequest,
  SaleProductPrice,
} from '../../../clients/quipli-service/pricing/pricing';
import { Field } from '../../../components/field/field';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Modal } from '../../../components/modal/modal';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../util/toasts';
import { ApiError } from '../../../clients/axios-instance';
import './sale-price-modal.scss';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { Button } from '../../../components/form/button';

export const SalePriceModal = ({
  productId,
  price,
  isOpen,
  close,
  locationOptions,
}: {
  productId: number;
  price?: SaleProductPrice;
  isOpen: boolean;
  close: (refresh?: boolean) => void;
  locationOptions: { value: number; label: string }[];
}) => {
  const { createPrice, editPrice } = useAPI();

  const initPriceState = { productId, showPriceOnProductCard: true };
  const [priceState, setPriceState] = useState<{
    id?: string;
    productId: number;
    price?: number;
    from?: DateTime;
    to?: DateTime;
    name?: string;
  }>(initPriceState);
  const [selectedLocations, setSelectedLocations] = useState<{ value: number; label: string }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLocationSpecific, setIsLocationSpecific] = useState<boolean>(
    price && price.locationIds ? price.locationIds.length > 0 : false,
  );

  useEffect(() => {
    if (price) {
      setPriceState({
        id: price.id,
        productId,
        price: price.price / 100,
        from: price.from,
        to: price.to,
        name: price.name,
      });
      setIsLocationSpecific(price && price.locationIds ? price.locationIds.length > 0 : false);
      setSelectedLocations(locationOptions.filter((option) => price.locationIds?.includes(option.value)));
    } else {
      resetState();
    }
  }, [price]);

  const isSaveDisabled: boolean = priceState.price === undefined;
  const resetState = () => {
    setPriceState(initPriceState);
    setSelectedLocations([]);
    setIsLocationSpecific(false);
  };

  const savePrice = () => {
    if (isSaveDisabled) {
      throw new Error('Missing required fields to save sale price');
    }
    setIsSaving(true);
    const body = {
      type: OrderType.Sale,
      productId: priceState.productId,
      price: +(priceState.price! * 100).toFixed(0),
      from: priceState.from?.startOf('day'),
      to: priceState.to?.endOf('day'),
      name: priceState.name,
      featured: true,
      locationIds: isLocationSpecific ? selectedLocations.map((l) => l.value) : undefined,
    };
    (price?.id ? editPrice(price.id, body as EditProductPriceRequest) : createPrice(body as CreateProductPriceRequest))
      .then(() => {
        toast('Successfully created sale price', 'success');
        resetState();
        close(true);
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error creating sale price';
        toast(errMsg, 'error');
      })
      .finally(() => setIsSaving(false));
  };

  const cancel = () => {
    resetState();
    close();
  };

  return (
    <div className="sale-price-modal">
      <Modal isOpen={isOpen} title="Add sale price" close={cancel}>
        <div className="space-y-5 edit-sale-price-container font-sans">
          <div>
            <Field
              type="text"
              label="Name"
              value={priceState.name}
              onChange={(name: string) => setPriceState({ ...priceState, name })}
            />
          </div>

          <div className="flex text-gray-800">
            <Checkbox
              isChecked={isLocationSpecific}
              onChange={(isChecked) => setIsLocationSpecific(isChecked)}
              label="This price applies to specific location(s)"
            />
          </div>

          {isLocationSpecific && (
            <div className="grid grid-cols-5 items-center gap-2">
              <span className="col-span-2 ml-1 text-sm font-semibold text-gray-500">Locations</span>
              <div className="col-span-3">
                <Select
                  id="locations-select"
                  isMulti={true}
                  options={locationOptions}
                  value={selectedLocations}
                  onChange={(selected) => {
                    setSelectedLocations(selected as { value: number; label: string }[]);
                  }}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      margin: '0px',
                      padding: '0px',
                      fontStyle: 'normal',
                      borderColor: 'rgb(209 213 219)',
                    }),
                    menuList: (base) => ({
                      ...base,

                      '::-webkit-scrollbar': {
                        width: '4px',
                        height: '0px',
                      },
                      '::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                      },
                      '::-webkit-scrollbar-thumb': {
                        background: '#888',
                      },
                      '::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                      },
                    }),
                  }}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-5 items-center gap-2">
            <div className="col-span-2 ml-1">
              <span className="text-sm font-semibold text-gray-500">Sale Price, $*</span>
            </div>
            <div className="col-span-3">
              <Field
                className="price-field"
                type="number"
                value={priceState.price}
                onChange={(p: number) => {
                  setPriceState({ ...priceState, price: p });
                }}
                maximumFractionDigits={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-5 items-center gap-2">
            <div className="col-span-2 ml-1">
              <span className="text-sm font-semibold text-gray-500">Starts On</span>
            </div>
            <div className="col-span-3">
              <DatePicker value={priceState.from} onChange={(from) => setPriceState({ ...priceState, from })} />
            </div>
          </div>

          <div className="grid grid-cols-5 items-center gap-2">
            <div className="col-span-2 ml-1">
              <span className="text-sm font-semibold text-gray-500">Ends On</span>
            </div>
            <div className="col-span-3">
              <DatePicker value={priceState.to} onChange={(to) => setPriceState({ ...priceState, to })} />
            </div>
          </div>
        </div>

        <div className="inset-x-0 bottom-0 mt-36 flex justify-end button-group">
          <Button className="button-secondary" onClick={cancel}>
            Cancel
          </Button>
          <Button className="button-primary" onClick={savePrice} disabled={isSaveDisabled || isSaving}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};
